import { supabase } from '../supabaseClient';
import { getSupabaseStorageBucket } from '../config';

const normalizeExtension = (filename = '') => {
  const rawExtension = filename.includes('.') ? filename.split('.').pop() : '';
  const safeExtension = (rawExtension || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
  return safeExtension || 'jpg';
};

export const buildItemImagePath = (venueId, itemId, originalFileName) => {
  const extension = normalizeExtension(originalFileName);
  return `venues/${venueId}/items/item-${itemId}.${extension}`;
};

/**
 * Extract file path from Supabase storage URL
 * @param {string} url - The full Supabase storage URL
 * @returns {string|null} - The file path or null if extraction fails
 */
export const extractFilePathFromUrl = (url) => {
  if (!url) return null;
  try {
    // Extract path after '/object/public/<bucket>/'
    const match = url.match(/\/object\/public\/[^/]+\/(.+)/);
    return match ? match[1] : null;
  } catch (error) {
    console.error('Error extracting file path from URL:', error);
    return null;
  }
};

/**
 * Delete an image from storage using its URL
 * @param {string} imageUrl - The full image URL
 * @param {string} bucketName - The storage bucket name
 * @returns {Promise<boolean>} - Success status
 */
export const deleteImageFromStorage = async (imageUrl, bucketName = getSupabaseStorageBucket()) => {
  if (!imageUrl) return false;
  
  try {
    const filePath = extractFilePathFromUrl(imageUrl);
    if (!filePath) {
      console.warn('Could not extract file path from URL:', imageUrl);
      return false;
    }

    console.log('Deleting image file:', filePath);
    const { error } = await supabase.storage
      .from(bucketName)
      .remove([filePath]);

    if (error) {
      console.error('Failed to delete image:', error);
      return false;
    }

    console.log('Image deleted successfully:', filePath);
    return true;
  } catch (error) {
    console.error('Error during image deletion:', error);
    return false;
  }
};

/**
 * Upload an image and optionally delete the old one
 * @param {File} imageFile - The image file to upload
 * @param {string} venueId - The venue ID
 * @param {string} oldImageUrl - The old image URL to delete (optional)
 * @param {string} bucketName - The storage bucket name
 * @returns {Promise<string|null>} - The new image URL or null if failed
 */
export const uploadImageWithCleanup = async (
  imageFile,
  venueId,
  oldImageUrl = null,
  bucketName = getSupabaseStorageBucket(),
  itemId = null
) => {
  try {
    // Delete old image if provided
    if (oldImageUrl) {
      await deleteImageFromStorage(oldImageUrl, bucketName);
    }

    // Upload new image
    const stableItemId = itemId ?? Date.now();
    const fileName = buildItemImagePath(venueId, stableItemId, imageFile.name);
    const { error } = await supabase.storage
      .from(bucketName)
      .upload(fileName, imageFile, { upsert: true });

    if (error) {
      console.error('Error uploading image:', error);
      return null;
    }

    // Get public URL
    const { data: publicURL, error: urlError } = supabase.storage
      .from(bucketName)
      .getPublicUrl(fileName);

    if (urlError || !publicURL) {
      console.error('Error getting image URL:', urlError);
      return null;
    }

    return publicURL.publicUrl;
  } catch (error) {
    console.error('Error in image upload with cleanup:', error);
    return null;
  }
};

/**
 * Cleanup orphaned images (images that exist in storage but not in database)
 * This could be run as a periodic cleanup job
 * @param {string} venueId - The venue ID to clean up
 * @param {Array} activeImageUrls - Array of image URLs currently in use
 * @returns {Promise<number>} - Number of images cleaned up
 */
export const cleanupOrphanedImages = async (venueId, activeImageUrls = []) => {
  try {
    const bucketName = getSupabaseStorageBucket();
    const folderPath = `venues/${venueId}/items/`;
    
    // List all files in the venue's items folder
    const { data: files, error } = await supabase.storage
      .from(bucketName)
      .list(folderPath);

    if (error) {
      console.error('Error listing files:', error);
      return 0;
    }

    if (!files || files.length === 0) {
      return 0;
    }

    // Extract active file paths from URLs
    const activeFilePaths = activeImageUrls
      .map(url => extractFilePathFromUrl(url))
      .filter(Boolean);

    // Find orphaned files
    const orphanedFiles = files.filter(file => {
      const fullPath = folderPath + file.name;
      return !activeFilePaths.includes(fullPath);
    });

    // Delete orphaned files
    if (orphanedFiles.length > 0) {
      const pathsToDelete = orphanedFiles.map(file => folderPath + file.name);
      
      const { error: deleteError } = await supabase.storage
        .from(bucketName)
        .remove(pathsToDelete);

      if (deleteError) {
        console.error('Error deleting orphaned files:', deleteError);
        return 0;
      }

      console.log(`Cleaned up ${orphanedFiles.length} orphaned images`);
      return orphanedFiles.length;
    }

    return 0;
  } catch (error) {
    console.error('Error during orphaned image cleanup:', error);
    return 0;
  }
};
